import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, PencilSimple, Trash, ArrowSquareOut } from "@phosphor-icons/react";

export default function AdminPYQPapers() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/pyq-papers"); setItems(data); }
        catch (e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openEdit = (p) => {
        setEditing(p.paper_id);
        setForm({ title: p.title, year: p.year, paper_number: p.paper_number || "", paper_type: p.paper_type, linked_paper_id: p.linked_paper_id || "", stage: p.stage });
    };

    const save = async () => {
        try {
            const payload = { ...form };
            if (payload.linked_paper_id === "") payload.linked_paper_id = null;
            await api.put(`/admin/pyq-papers/${editing}`, payload);
            toast.success("Updated"); setEditing(null); setForm({}); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (paperId, hard = false) => {
        const msg = hard ? "Permanently delete this paper (cannot be undone)?" : "Soft-delete this paper?";
        if (!window.confirm(msg)) return;
        try { await api.delete(`/admin/pyq-papers/${paperId}`, { params: { hard } }); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Delete failed"); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <div className="text-sm text-slate-500">Upload new papers from the existing upload page.</div>
                <a href="/admin/pyq/upload" data-testid="pyq-upload-link" className="ml-auto btn-primary !py-2 !px-4"><UploadSimple size={16} weight="bold" /> Upload paper</a>
            </div>

            {editing && (
                <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 space-y-3">
                    <h3 className="font-heading font-bold">Edit paper metadata</h3>
                    <div className="grid md:grid-cols-3 gap-3">
                        <input placeholder="Title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                        <input type="number" placeholder="Year" value={form.year || ""} onChange={(e) => setForm({ ...form, year: e.target.value ? parseInt(e.target.value) : null })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                        <input placeholder="Paper number" value={form.paper_number || ""} onChange={(e) => setForm({ ...form, paper_number: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                        <select value={form.paper_type || "question"} onChange={(e) => setForm({ ...form, paper_type: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                            <option value="question">Question paper</option><option value="answer_key">Answer key</option>
                        </select>
                        <select value={form.stage || "prelims"} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                            <option value="prelims">Prelims</option><option value="mains">Mains</option><option value="interview">Interview</option>
                        </select>
                        <input placeholder="Linked question paper_id (for answer keys)" value={form.linked_paper_id || ""} onChange={(e) => setForm({ ...form, linked_paper_id: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm md:col-span-3" />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => { setEditing(null); setForm({}); }} className="btn-ghost !py-2 !px-4">Cancel</button>
                        <button data-testid="pyq-save" onClick={save} className="btn-primary !py-2 !px-4">Save</button>
                    </div>
                </div>
            )}

            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b-2 border-[#BFD3FF] text-xs uppercase tracking-widest font-bold text-slate-600">{loading ? "Loading…" : `${items.length} paper(s)`}</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
                            <tr><th className="px-4 py-2 font-bold">Board</th><th className="px-4 py-2 font-bold">Stage</th><th className="px-4 py-2 font-bold">Year</th><th className="px-4 py-2 font-bold">Type</th><th className="px-4 py-2 font-bold">Title</th><th className="px-4 py-2 font-bold">State</th><th className="px-4 py-2 font-bold text-right w-32">Actions</th></tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.paper_id} className={`border-t border-slate-100 hover:bg-slate-50 ${p.is_deleted ? "opacity-50" : ""}`}>
                                    <td className="px-4 py-3 uppercase text-xs font-bold text-[#1E3A8A]">{p.exam_id}</td>
                                    <td className="px-4 py-3 text-xs capitalize">{p.stage}</td>
                                    <td className="px-4 py-3 text-xs tabular-nums">{p.year}</td>
                                    <td className="px-4 py-3 text-xs">{p.paper_type === "answer_key" ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">KEY</span> : <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">QP</span>}</td>
                                    <td className="px-4 py-3 text-xs max-w-xs truncate">{p.title}</td>
                                    <td className="px-4 py-3 text-xs">{p.is_deleted ? <span className="text-red-600 font-semibold">deleted</span> : <span className="text-emerald-700 font-semibold">live</span>}</td>
                                    <td className="px-4 py-3 text-right">
                                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pyq-papers/${p.paper_id}/download`} target="_blank" rel="noreferrer" className="p-1.5 inline-block text-slate-500 hover:text-[#1E3A8A]" title="View"><ArrowSquareOut size={14} /></a>
                                        <button onClick={() => openEdit(p)} className="p-1.5 text-slate-500 hover:text-[#1E3A8A]"><PencilSimple size={14} /></button>
                                        <button onClick={() => remove(p.paper_id, false)} className="p-1.5 text-slate-500 hover:text-red-600" title="Soft delete"><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No papers.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
